import React, { useEffect, useState } from 'react';
import { Coins, Loader2, Save } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useToast } from '../../hooks/use-toast';
import { adminGetSettings, adminUpdateSettings } from '../../api/admin_phase6';

/**
 * TipPresetsEditor — admin config for the Tip Jar quick-tip buttons.
 *
 * Contract:
 *  - Values are stored in `app_settings.tip_presets` as an array of
 *    integer Credit amounts (NOT euro).
 *  - The Tip Jar in the Live pages consumes them from
 *    `/api/public/config.tip_presets` and renders each as a quick
 *    button ("N crediti"). Sending a tip debits the user's
 *    wallet_balance directly — NO Stripe checkout is involved.
 *
 * UI copy is in Italian and repeatedly clarifies that values are
 * Credits (not €) — the previous label "Tip Jar presets (Credits)"
 * proved ambiguous in production and led admins to interpret
 * "2, 5, 10" as EUR amounts.
 *
 * Validation:
 *  - only positive integers
 *  - min 1, max 8 values
 *  - no zero, no negatives
 *  - no duplicates
 *  - upper cap 10 000 Credits per preset (safety rail; the highest
 *    creator preset is 990)
 */
const TipPresetsEditor = () => {
  const { toast } = useToast();
  const [raw, setRaw] = useState('2, 5, 10, 25, 50');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const s = await adminGetSettings();
        if (mounted && Array.isArray(s?.tip_presets) && s.tip_presets.length > 0) {
          setRaw(s.tip_presets.join(', '));
        }
      } catch (_e) { /* keep defaults */ }
      finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, []);

  const parse = () => {
    const tokens = raw.split(/[,\s]+/).map((s) => s.trim()).filter(Boolean);
    const parsed = [];
    for (const tok of tokens) {
      if (!/^[0-9]+$/.test(tok)) {
        return { error: `"${tok}" non è un intero positivo — usa solo numeri interi (es. 5, 10, 25).` };
      }
      const n = parseInt(tok, 10);
      if (n <= 0) {
        return { error: 'Ogni preset deve essere un intero > 0. Nessun valore zero o negativo.' };
      }
      if (n > 10000) {
        return { error: `Il valore ${n} è troppo alto (max 10 000 crediti per preset).` };
      }
      parsed.push(n);
    }
    if (parsed.length < 1) return { error: 'Inserisci almeno 1 preset.' };
    if (parsed.length > 8) return { error: 'Massimo 8 preset. Ne hai inseriti ' + parsed.length + '.' };
    const set = new Set(parsed);
    if (set.size !== parsed.length) return { error: 'Non sono ammessi valori duplicati.' };
    return { arr: parsed };
  };

  const save = async () => {
    const { arr, error } = parse();
    if (error) {
      toast({ title: 'Preset non validi', description: error, variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      await adminUpdateSettings({ tip_presets: arr });
      toast({
        title: 'Preset mance salvati',
        description: `${arr.length} valori attivi: ${arr.join(', ')} crediti.`,
      });
    } catch (e) {
      toast({
        title: 'Errore salvataggio',
        description: e?.response?.data?.detail || String(e),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid="tip-presets-editor">
      <div className="mb-2 flex items-center gap-2">
        <Coins className="h-5 w-5 gold-text" />
        <h3 className="text-base font-semibold">Preset mance Tip Jar</h3>
      </div>
      <p className="mb-3 text-xs text-muted-foreground">
        Valori espressi in <b className="text-foreground">crediti</b>. Questi importi
        appariranno come pulsanti rapidi nella Live Tip Jar (spettatori → Voce Pro).
        La mancia debita il wallet dello spettatore in crediti — <b>nessun checkout Stripe</b> diretto.
      </p>

      <label className="mb-1 block text-xs font-semibold" htmlFor="tip-presets-input">
        Crediti preset
      </label>
      <div className="flex flex-wrap gap-2">
        <Input
          id="tip-presets-input"
          value={raw}
          disabled={loading}
          onChange={(e) => setRaw(e.target.value)}
          placeholder="Es. 2, 5, 10, 25, 50"
          className="flex-1 min-w-[220px]"
          data-testid="tip-presets-input"
        />
        <Button
          onClick={save}
          disabled={saving || loading}
          className="gold-gradient text-black"
          data-testid="tip-presets-save"
        >
          {saving ? (
            <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
          ) : (
            <Save className="mr-1.5 h-4 w-4" />
          )}
          Salva preset mance
        </Button>
      </div>

      <p className="mt-2 rounded-lg bg-amber-500/10 p-2.5 text-[11px] text-amber-100">
        <b>Esempio:</b> <span className="font-mono">10</span> ={' '}
        <span className="font-mono">10 crediti</span>, non <span className="font-mono">€10</span>.
        Vincoli: interi positivi, massimo 8 valori, senza duplicati.
      </p>
    </div>
  );
};

export default TipPresetsEditor;
